/*
  Mije Seoul — cart behaviour (drawer + /cart page).

  - Any successful POST to /cart/add.js — PDP, bundle, saved, cross-sell,
    future callers — opens the drawer with fresh contents. Implemented as a
    fetch interception so no other worker's file needs touching; the drawer
    re-renders via the Section Rendering API (?sections=mije-cart-drawer).
  - Qty steppers and Remove go through /cart/change.js; Remove shows a 5s
    Undo row (optimistic, per the pt2 interaction spec).
  - Header BAG count (desktop badge + mobile "Bag n") stays live.
  - The /cart page re-renders its own section after mutations; without JS it
    falls back to the native cart form (updates[] inputs + Remove links).
  - The delivery meter picks its market from the detected country. Liquid
    renders all three markets (see snippets/mije-cart-meter.liquid); this file
    unhides one, on load, whenever the country resolves, and after every
    re-render — the drawer and the /cart page both replace the node.
*/
(function () {
  'use strict';

  if (window.MijeCart) return;
  window.MijeCart = true;

  var DRAWER_SECTION = 'mije-cart-drawer';
  var pendingUndo = null; // { id, quantity, title, timer }

  function drawerWrapper() {
    return document.getElementById('shopify-section-' + DRAWER_SECTION);
  }

  function panel() {
    return document.querySelector('[data-cart-panel]');
  }

  var lastTrigger = null;

  /* ------------------------------------------------------------ open/close */

  function setOpen(open, trigger) {
    var p = panel();
    var scrim = document.querySelector('[data-cart-scrim]');
    if (!p) return;
    if (open && trigger) lastTrigger = trigger;
    p.classList.toggle('is-open', open);
    if (scrim) scrim.classList.toggle('is-open', open);
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) {
      p.focus({ preventScroll: true });
    } else if (lastTrigger && document.contains(lastTrigger)) {
      lastTrigger.focus({ preventScroll: true });
      lastTrigger = null;
    }
  }

  function isOpen() {
    var p = panel();
    return !!(p && p.classList.contains('is-open'));
  }

  /* ------------------------------------------------------------- bag count */

  function updateBagCount(n) {
    document.querySelectorAll('.mj-header__utils .mj-header__bag').forEach(function (bag) {
      var badge = bag.querySelector('.mj-header__count');
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'mj-header__count mj-circle';
          bag.appendChild(badge);
        }
        badge.textContent = n;
      } else if (badge) {
        badge.remove();
      }
    });
    document.querySelectorAll('.mj-header__utils--mobile .mj-header__bag').forEach(function (bag) {
      bag.textContent = 'Bag ' + n;
    });
  }

  /* -------------------------------------------------- delivery meter market */

  /*
    Which of the rendered markets to show. The country is asked for exactly
    once, from MijeCurrency — the single geo source in the theme (see the
    COUNTRY vs CURRENCY note in mije-currency.js). It deliberately keys off
    COUNTRY and not the display currency: an SG shopper reading MYR still gets
    free SG delivery, and an MY shopper reading SGD still gets the MY meter.

    The states, and the difference between the last two matters:
      'SG'  → the SG line
      'MY'  → the MY line (free, no minimum — same shape as SG)
      'AU'  → the A$99 meter (threshold converted from the pinned rate)
      ''    → resolved, but no country: keep the neutral fallback
      null  → not resolved yet: keep the neutral fallback and wait for the
              mije:currency event, which fires again once the lookup lands
  */
  var MARKETS = { SG: 'SG', MY: 'MY', AU: 'AU' };

  function paintMeters() {
    var country = window.MijeCurrency && window.MijeCurrency.country
      ? window.MijeCurrency.country()
      : null;
    var market = MARKETS[String(country || '').toUpperCase()] || 'OTHER';

    document.querySelectorAll('[data-cart-meter]').forEach(function (meter) {
      meter.querySelectorAll('[data-meter-market]').forEach(function (block) {
        block.hidden = block.dataset.meterMarket !== market;
      });
      /* what actually got shown, for verification and for anything downstream */
      meter.dataset.meterResolved = market;
    });
  }

  /* Fires on first paint and again when the country lands mid-session. */
  window.addEventListener('mije:currency', paintMeters);

  /* ------------------------------------------------- PREORDER10 nudge */

  /*
    The bag nudge is rendered by Liquid when a pre-order line is present and
    the CART carries no discount code. But a code entered in the /cart box (or
    arriving on a /discount/CODE link, which is how the welcome emails apply
    theirs) is held by Shopify in the discount_code cookie until checkout and
    never shows on the cart object - measured on DEV: cart.discount_codes stays
    empty after /discount/CODE. So the "no code applied" half of the rule is
    finished here: if that cookie (or this session's own Apply) says a code is
    waiting, the nudge is removed after every render.
  */
  var CODE_FLAG = 'mije.discount.applied';

  function codeWaiting() {
    try { if (window.sessionStorage.getItem(CODE_FLAG)) return true; } catch (e) {}
    return /(?:^|;\s*)discount_code=[^;]+/.test(document.cookie || '');
  }

  function paintNudge() {
    if (!codeWaiting()) return;
    document.querySelectorAll('[data-preorder-nudge="drawer"], [data-preorder-nudge="page"]').forEach(function (n) {
      n.remove();
    });
  }

  /* -------------------------------------------------------------- refresh */

  function refreshDrawer(thenOpen) {
    var wrapper = drawerWrapper();
    if (!wrapper) return Promise.resolve();
    var wasOpen = isOpen() || thenOpen;
    return window
      .fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + '?sections=' + DRAWER_SECTION : '/?sections=' + DRAWER_SECTION)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[DRAWER_SECTION];
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('[data-cart-drawer-root]');
        var current = wrapper.querySelector('[data-cart-drawer-root]');
        if (fresh && current) current.replaceWith(fresh);
        var count = fresh ? Number(fresh.dataset.cartCount || 0) : 0;
        updateBagCount(count);
        fillSavedCount();
        paintMeters(); // the replaced node carries the server-rendered fallback
        paintNudge();
        if (wasOpen) setOpen(true);
        return reconcilePreorder(); // the fresh node carries a fresh plan
      })
      .catch(function () {});
  }

  function refreshPage() {
    var page = document.querySelector('[data-cart-page]');
    if (!page) return Promise.resolve();
    var wrapper = page.closest('.shopify-section');
    if (!wrapper || !wrapper.id) return Promise.resolve();
    var sectionId = wrapper.id.replace('shopify-section-', '');
    return window
      .fetch(window.location.pathname + '?sections=' + sectionId)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('[data-cart-page]');
        if (fresh) page.replaceWith(fresh);
        paintNudge();
        fillSavedCount();
        paintMeters(); // the replaced node carries the server-rendered fallback
      })
      .catch(function () {});
  }

  function refreshAll(thenOpenDrawer) {
    return Promise.all([refreshDrawer(thenOpenDrawer), refreshPage()]);
  }

  /* ------------------------------------------------- pre-order reconcile */

  /*
    Every cart line must carry properties[Pre-order] = the ship promise when,
    and only when, it is a pre-order purchase. The product page stamps it at
    add time, but a line can reach the bag by a dozen other routes that never
    see the product state: "Add the look", bundle cards, cross-sell, cart
    recommendations (a native form POST), the saved page, journal embeds,
    Undo, a cart restored from a link. Rather than teach each of them the rule
    (and miss the next one), the rule is decided ONCE, by Liquid, for the cart
    as it actually is (snippets/mije-preorder-want.liquid) and published as a
    plan in the drawer section, which is on every page:

        [{ key, id, quantity, want, has, properties }, ...]

    This walks the plan and fixes the first line where want !== has through
    /cart/change.js, re-renders (line keys change when properties change, and
    two lines that become identical merge), and repeats until the plan is
    clean. It runs on load and after every drawer re-render, so it also covers
    the native POST that lands on /cart. Checkout and the wallet buttons are
    locked while it runs: the discount Function and the order both read the
    property, so nobody leaves for checkout with a half-stamped bag.
  */
  var PREORDER_KEY = 'Pre-order';
  var reconcileRun = null; // the in-flight promise, so callers share one pass
  var cartBusy = 0;

  function lockCheckout(on) {
    cartBusy += on ? 1 : -1;
    if (cartBusy < 0) cartBusy = 0;
    document.documentElement.classList.toggle('mj-cart-busy', cartBusy > 0);
  }

  function readPlan() {
    var el = document.querySelector('[data-preorder-plan]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function firstMismatch() {
    var plan = readPlan();
    for (var i = 0; i < plan.length; i++) {
      if ((plan[i].want || '') !== (plan[i].has || '')) return plan[i];
    }
    return null;
  }

  function fetchDrawerRoot() {
    return origFetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + '?sections=' + DRAWER_SECTION)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var wrapper = drawerWrapper();
        var html = data[DRAWER_SECTION];
        if (!wrapper || !html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('[data-cart-drawer-root]');
        var current = wrapper.querySelector('[data-cart-drawer-root]');
        if (!fresh || !current) return;
        var wasOpen = isOpen();
        current.replaceWith(fresh);
        updateBagCount(Number(fresh.dataset.cartCount || 0));
        fillSavedCount();
        paintMeters();
        paintNudge();
        if (wasOpen) setOpen(true);
      });
  }

  function reconcilePreorder() {
    if (reconcileRun) return reconcileRun;
    if (!firstMismatch()) return Promise.resolve(false);

    var passes = 0;
    function step() {
      var bad = firstMismatch();
      if (!bad || passes >= 12) return Promise.resolve();
      passes += 1;
      var props = {};
      Object.keys(bad.properties || {}).forEach(function (k) {
        if (k !== PREORDER_KEY) props[k] = bad.properties[k];
      });
      var post = function (url, body) {
        return origFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }).then(function (r) { if (!r.ok) throw new Error('reconcile failed'); });
      };
      var fix;
      if (bad.want) {
        props[PREORDER_KEY] = bad.want;
        fix = post('/cart/change.js', { id: bad.key, quantity: bad.quantity, properties: props });
      } else if (Object.keys(props).length) {
        /* /cart/change.js REPLACES the property set: leaving the key out
           removes it (measured on DEV) */
        fix = post('/cart/change.js', { id: bad.key, quantity: bad.quantity, properties: props });
      } else {
        /* the stamp is the line's ONLY property, and an empty properties
           object is ignored by /cart/change.js while '' or null leave a
           blank "Pre-order" key behind (all three measured on DEV). The one
           clean way to an unstamped line is to re-add it bare. */
        fix = post('/cart/change.js', { id: bad.key, quantity: 0 }).then(function () {
          return post('/cart/add.js', { items: [{ id: bad.id, quantity: bad.quantity }] });
        });
      }
      return fix.then(fetchDrawerRoot).then(step);
    }

    lockCheckout(true);
    reconcileRun = step()
      .catch(function () {})
      .then(function () { return refreshPage(); })
      .then(function () {
        reconcileRun = null;
        lockCheckout(false);
        document.dispatchEvent(new CustomEvent('mije:cart:reconciled', { detail: { passes: passes } }));
        return passes > 0;
      });
    return reconcileRun;
  }

  /* a Checkout tap that lands while the bag is being stamped waits for it */
  document.addEventListener('submit', function (e) {
    if (!cartBusy) return;
    var form = e.target;
    var submitter = e.submitter;
    if (!form || !form.querySelector || !form.querySelector('[name="checkout"]')) return;
    e.preventDefault();
    var tries = 0;
    (function wait() {
      if (cartBusy && tries++ < 100) return setTimeout(wait, 50);
      if (submitter && document.contains(submitter)) return submitter.click();
      var b = document.querySelector('.mj-cart.is-open [name="checkout"], [data-cart-page] [name="checkout"]');
      if (b) b.click();
    })();
  }, true);

  /* -------------------------------------------- intercept /cart/add.js */

  var origFetch = window.fetch.bind(window);
  var suppressOpen = false; // undo re-adds shouldn't force the drawer open on /cart

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
    var result = origFetch(input, init);
    if (isAdd) {
      lockCheckout(true); // held until the re-render and the reconcile land
      var unlock = function () { lockCheckout(false); };
      result = result.then(function (res) {
        if (res.ok) {
          var open = !suppressOpen;
          suppressOpen = false;
          refreshAll(open).then(unlock, unlock);
          // analytics seam: expose the added items to any listener
          // (mije-analytics.js) without coupling this file to a vendor
          res.clone().json().then(function (data) {
            document.dispatchEvent(new CustomEvent('mije:cart:added', { detail: data }));
          }).catch(function () {});
        } else {
          unlock();
        }
        return res;
      }, function (err) {
        unlock();
        throw err;
      });
    }
    return result;
  };

  /* --------------------------------------------------------- cart change */

  function changeLine(line, quantity) {
    return origFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    }).then(function (r) {
      if (!r.ok) throw new Error('change failed');
      return r.json();
    });
  }

  function showUndoRow(title, variantId, quantity) {
    clearUndo();
    // when the removal emptied the cart, the re-rendered markup has no lines
    // container — host the undo row at the top of the empty state instead
    var targets = document.querySelectorAll('[data-cart-lines]');
    if (!targets.length) targets = document.querySelectorAll('.mj-cart__empty, .mjcp-empty');
    targets.forEach(function (list) {
      var row = document.createElement('div');
      row.className = 'mj-cart__undo';
      row.setAttribute('data-undo-row', '');
      row.innerHTML =
        '<span>Removed — ' + title + '</span>' +
        '<button type="button" data-undo-add="' + variantId + '" data-undo-qty="' + quantity + '">Undo</button>';
      list.insertBefore(row, list.firstChild);
    });
    pendingUndo = {
      timer: setTimeout(clearUndo, 5000),
    };
  }

  function clearUndo() {
    if (pendingUndo && pendingUndo.timer) clearTimeout(pendingUndo.timer);
    pendingUndo = null;
    document.querySelectorAll('[data-undo-row]').forEach(function (r) { r.remove(); });
  }

  function removeLine(lineEl, line) {
    var removeEl = lineEl.querySelector('[data-remove-line]');
    var title = removeEl ? removeEl.dataset.lineTitle : 'item';
    var variantId = removeEl ? removeEl.dataset.variantId : null;
    var qty = removeEl ? Number(removeEl.dataset.lineQty || 1) : 1;
    lineEl.style.opacity = '0.4'; // optimistic
    changeLine(line, 0)
      .then(function () { return refreshAll(false); })
      .then(function () {
        if (variantId) showUndoRow(title, variantId, qty);
      })
      .catch(function () { refreshAll(false); });
  }

  /* ------------------------------------------------------- saved count */

  function fillSavedCount() {
    var n = 0;
    try {
      n = (JSON.parse(localStorage.getItem('mije.wishlist.v1') || '[]') || []).length;
    } catch (e) { n = 0; }
    document.querySelectorAll('[data-saved-count]').forEach(function (el) {
      el.textContent = n > 0 ? ' (' + n + ')' : '';
    });
  }

  /* ------------------------------------------------------------- events */

  document.addEventListener('click', function (e) {
    var t = e.target;

    /* open from the header bag link (desktop + mobile) */
    var bag = t.closest('.mj-header__bag');
    if (bag && panel()) {
      e.preventDefault();
      refreshDrawer(true);
      lastTrigger = bag;
      return;
    }

    /* close */
    if (t.closest('[data-cart-close]') || t.closest('[data-cart-scrim]')) {
      e.preventDefault();
      setOpen(false);
      return;
    }

    /* stepper */
    var stepBtn = t.closest('[data-step]');
    if (stepBtn && !stepBtn.disabled) {
      var stepper = stepBtn.closest('.mj-cart__stepper');
      var line = Number(stepper.dataset.line);
      var qty = Number(stepper.dataset.qty);
      var next = qty + Number(stepBtn.dataset.step);
      var lineEl = stepper.closest('[data-cart-line]');
      if (next <= 0) {
        removeLine(lineEl, line); // "−" at qty 1 → remove with Undo as the confirm
      } else {
        stepper.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
        changeLine(line, next)
          .then(function () { return refreshAll(false); })
          .catch(function () { refreshAll(false); });
      }
      return;
    }

    /* remove */
    var removeEl = t.closest('[data-remove-line]');
    if (removeEl) {
      e.preventDefault();
      removeLine(removeEl.closest('[data-cart-line]'), Number(removeEl.dataset.removeLine));
      return;
    }

    /* undo */
    var undoBtn = t.closest('[data-undo-add]');
    if (undoBtn) {
      var id = Number(undoBtn.dataset.undoAdd);
      var q = Number(undoBtn.dataset.undoQty || 1);
      clearUndo();
      if (document.querySelector('[data-cart-page]') && !isOpen()) suppressOpen = true;
      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: q }] }),
      });
      return;
    }

    /* bundle "Add the look" (mije_bundle carousel cards) */
    var bundleBtn = t.closest('[data-bundle-add]');
    if (bundleBtn && !bundleBtn.disabled) {
      var ids = bundleBtn.dataset.bundleAdd.split(',').filter(Boolean).map(Number);
      if (!ids.length) return;
      var prev = bundleBtn.textContent;
      bundleBtn.disabled = true;
      bundleBtn.textContent = bundleBtn.dataset.addingLabel || 'Adding…';
      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids.map(function (id) { return { id: id, quantity: 1 }; }) }),
      }).then(function () {
        bundleBtn.textContent = 'Added ✓';
      }).catch(function () {
        bundleBtn.textContent = 'Try again';
      }).then(function () {
        setTimeout(function () { bundleBtn.disabled = false; bundleBtn.textContent = prev; }, 1800);
      });
      return;
    }

    /* cross-sell add */
    var xsell = t.closest('[data-xsell-add]');
    if (xsell) {
      xsell.disabled = true;
      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(xsell.dataset.xsellAdd), quantity: 1 }] }),
      });
      return;
    }

    /* copy a discount code (PREORDER10 nudge on the PDP, drawer and /cart).
       Clipboard only: the code is never applied on the shopper's behalf. */
    var copyBtn = t.closest('[data-copy-code]');
    if (copyBtn) {
      e.preventDefault();
      var codeText = copyBtn.dataset.copyCode || '';
      var done = function () {
        copyBtn.textContent = 'Copied';
        copyBtn.classList.add('is-copied');
        clearTimeout(copyBtn._mjT);
        copyBtn._mjT = setTimeout(function () {
          copyBtn.textContent = 'Copy';
          copyBtn.classList.remove('is-copied');
        }, 2000);
      };
      var legacy = function () {
        var ta = document.createElement('textarea');
        ta.value = codeText;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (err) {}
        ta.remove();
        done();
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(codeText).then(done, legacy);
      } else {
        legacy();
      }
      return;
    }

    /* discount apply — /discount/CODE stores the code for checkout; cart
       totals cannot reflect it pre-checkout, so say that honestly */
    var applyBtn = t.closest('[data-discount-apply]');
    if (applyBtn) {
      var input = document.querySelector('[data-discount-input]');
      var note = document.querySelector('[data-discount-note]');
      var code = input && input.value.trim();
      if (!code || !note) return;
      origFetch('/discount/' + encodeURIComponent(code), { method: 'GET' })
        .then(function () {
          note.hidden = false;
          note.classList.remove('mjcp-discount-note--error');
          note.textContent = 'Code saved — it applies at checkout.';
          /* a code is waiting for checkout now: the PREORDER10 nudge has
             done its job (see paintNudge) */
          try { window.sessionStorage.setItem(CODE_FLAG, code); } catch (err) {}
          paintNudge();
        })
        .catch(function () {
          note.hidden = false;
          note.classList.add('mjcp-discount-note--error');
          note.textContent = 'We couldn’t save that code — try again at checkout.';
        });
      return;
    }
  });

  /* typed qty on the /cart page inputs */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (!input) return;
    var stepper = input.closest('.mj-cart__stepper');
    var line = Number(stepper.dataset.line);
    var next = Math.max(0, Number(input.value) || 0);
    if (next === Number(stepper.dataset.qty)) return;
    if (next === 0) {
      removeLine(stepper.closest('[data-cart-line]'), line);
    } else {
      changeLine(line, next)
        .then(function () { return refreshAll(false); })
        .catch(function () { refreshAll(false); });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) setOpen(false);
  });

  fillSavedCount();
  /* The server-rendered plan: stamps a line that arrived by a native form
     POST (cart recommendations) or a restored cart, on the very next page. */
  reconcilePreorder();
  paintNudge();
  /* First paint. mije-currency.js is deferred too, so on a session that
     already knows the country this lands in the same frame as the fallback;
     on the very first view of a session the mije:currency listener above
     upgrades it as soon as the lookup resolves. */
  paintMeters();
})();
